import { useContext, useEffect, useState } from 'react';
import { AccountContext } from '../AccountContext';
import { useAsyncEffect } from '@app/hooks';
import { toCurrency, toFormattedDate } from '@app/utilities';
import styled from 'styled-components';
import Modal from 'react-overlays/Modal';
import { useCurrentAccountPosition, useGetReageAdjustments, useRequestManualReage } from '@app/sdk';

const ReageAdjustmentModal = styled(Modal)`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  width: fit-content;
  z-index: 1040;
  background-color: white;
  box-shadow: 0 5px 15px rgba(0, 0, 0, 0.5);
  padding: 20px;
`;

const RequestReageAdjustmentModal = styled(Modal)`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  width: fit-content;
  z-index: 1040;
  background-color: white;
  box-shadow: 0 5px 15px rgba(0, 0, 0, 0.5);
  padding: 20px;
`;

export default function ReageAdjustments() {
  const { accountId } = useContext(AccountContext);
  const [reageAdjustments, setReageAdjustments] = useState(undefined);
  const [reagePopupVisible, setReagePopupVisible] = useState(false);
  const [selectedReageAdjustment, setSelectedReageAdjustment] = useState<ReageAdjustment>(undefined);
  const [requestReagePopupVisible, setRequestReagePopupVisible] = useState(false);
  const [pending, setPending] = useState(false);
  const [showReageStrategy, setShowReageStrategy] = useState(false);
  const [showNewArrearLevel, setShowNewArrearLevel] = useState(false);
  const [reageType, setReageType] = useState('Full');
  const [reageStrategy, setReageStrategy] = useState('Forgive');
  const [newArrearLevel, setNewArrearLevel] = useState(0);
  const [error, setError] = useState(undefined);
  const [reageAdjustment, setReageAdjustment] = useState(0);
  const [accountLevel, setAccountLevel] = useState(0);

  const { mutateAsync: requestManualReage } = useRequestManualReage();
  const { data: reageAdjustmentData } = useGetReageAdjustments(accountId);
  const { data: currentAccountPosition } = useCurrentAccountPosition(accountId);

  useAsyncEffect(async () => {
    if (pending) {
      try {
        await requestManualReage({ accountId, reageType, reageStrategy, newArrearLevel });

        setPending(false);
        closeRequestReagePopup();
      } catch (err) {
        setPending(false);
        setError(err);
        console.log(`error: ${error}`);
      }
    }
  }, [pending]);

  useEffect(() => {
    setAccountLevel(currentAccountPosition?.arrears?.breakdown?.length);
  }, [currentAccountPosition]);

  useEffect(() => {
    const adj = reageAdjustmentData?.value.sort(
      (currentReageAdjustment: ReageAdjustment, nextReageAdjustment: ReageAdjustment) =>
        Date.parse(toFormattedDate(nextReageAdjustment.reageTimestamp, 'yyyy-MM-dd HH:mm:ss')) -
        Date.parse(toFormattedDate(currentReageAdjustment.reageTimestamp, 'yyyy-MM-dd HH:mm:ss'))
    );
    setReageAdjustments(adj);
  }, [accountId, reageAdjustmentData]);

  const showReagePopup = () => {
    setSelectedReageAdjustment(reageAdjustments[reageAdjustment]);
    setReagePopupVisible(true);
  };

  const closeReagePopup = () => {
    setReagePopupVisible(false);
  };

  const closeRequestReagePopup = () => {
    resetModal();
    setRequestReagePopupVisible(false);
  };

  const requestReageAdjustmentHandler = () => {
    setPending(true);
  };

  const reageTypeSelected = (reageTypeParam: string) => {
    setReageType(reageTypeParam);
    if (reageTypeParam === 'Partial') {
      setShowReageStrategy(true);
      setShowNewArrearLevel(true);
    } else {
      setShowReageStrategy(false);
      setReageStrategy('Forgive');
      setShowNewArrearLevel(false);
      setNewArrearLevel(0);
    }
  };

  const reageStrategySelected = (reageStrategyParam: string) => {
    setReageStrategy(reageStrategyParam);
  };

  const resetModal = () => {
    setError(undefined);
    setShowReageStrategy(false);
    setReageStrategy('Forgive');
    setShowNewArrearLevel(false);
    setNewArrearLevel(0);
  };

  return (
    <>
      <div className="ui card">
        <div className="custom-card-header">Re-age Adjustments</div>
        <div className="content">
          {reageAdjustments && (
            <>
              {reageAdjustments.length > 0 && (
                <div>
                  <select
                    name="reageAdjustments"
                    className="ui dropdown"
                    onChange={(e) => setReageAdjustment(parseInt(e.target.value))}>
                    {reageAdjustments.map((breakdown: ReageAdjustment, index: number) => (
                      <option key={index} value={index}>
                        {breakdown.trigger} - {toFormattedDate(breakdown.reageTimestamp, 'yyyy-MM-dd')}
                      </option>
                    ))}
                  </select>
                </div>
              )}
              <br />
              <button type="button" className="ui center aligned primary button" onClick={() => showReagePopup()}>
                <span>Show Adjustment Details</span>
              </button>
              <br />
              <ReageAdjustmentModal show={reagePopupVisible} onHide={closeReagePopup} aria-labelledby="modal-label">

                <div className="ui basic segment">
                  <div className="ui basic segment">
                    <button className="ui right floated icon button">
                      <i className="close icon" onClick={() => closeReagePopup()}></i>
                    </button>
                    <h3 className="ui left floated header">Re-age Adjustment</h3>
                  </div>
                  <br />
                  {selectedReageAdjustment && (
                    <>
                      <p>
                        Type: {selectedReageAdjustment.trigger} - {selectedReageAdjustment.reageType}
                      </p>
                      <p>Date: {toFormattedDate(selectedReageAdjustment.reageTimestamp, 'yyyy-MM-dd HH:mm:ss')}</p>
                      <div className="ui grid">
                        <div className="eight wide column">
                          <div className="row">
                            <div className="ui card">
                              <div className="custom-card-header">Previous Position</div>
                              <div className="content">
                                <div className="ui basic segment">
                                  <p>
                                    Total Due:{' '}
                                    {toCurrency(selectedReageAdjustment.previousAccountPosition.totalDue.value)}
                                  </p>
                                  <p>
                                    Current Due:{' '}
                                    {toCurrency(selectedReageAdjustment.previousAccountPosition.currentDue.value)}
                                  </p>
                                  <p>
                                    Arrears:{' '}
                                    {toCurrency(selectedReageAdjustment.previousAccountPosition.arrears.total.value)}
                                  </p>
                                  <p>
                                    Level: {selectedReageAdjustment.previousAccountPosition.arrears.breakdown.length}
                                  </p>
                                  {selectedReageAdjustment.previousAccountPosition.arrears.breakdown.length > 0 && (
                                    <div className="ui segments">
                                      <div className="ui basic segment">
                                        {selectedReageAdjustment.previousAccountPosition.arrears.breakdown.map(
                                          (arrear) => (
                                            <p key={arrear.age as any}>
                                              Age {arrear.age as any}: {toCurrency(arrear?.amount.value)}
                                            </p>
                                          )
                                        )}
                                      </div>
                                    </div>
                                  )}
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                        <div className="eight wide column">
                          <div className="row">
                            <div className="ui card">
                              <div className="custom-card-header">Adjusted Position</div>
                              <div className="content">
                                <div className="ui basic segment">
                                  <p>
                                    Total Due:{' '}
                                    {toCurrency(selectedReageAdjustment.reagedAccountPosition.totalDue.value)}
                                  </p>
                                  <p>
                                    Current Due:{' '}
                                    {toCurrency(selectedReageAdjustment.reagedAccountPosition.currentDue.value)}
                                  </p>
                                  <p>
                                    Arrears:{' '}
                                    {toCurrency(selectedReageAdjustment.reagedAccountPosition.arrears.total.value)}
                                  </p>
                                  <p>Level: {selectedReageAdjustment.reagedAccountPosition.arrears.breakdown.length}</p>
                                  {selectedReageAdjustment.reagedAccountPosition.arrears.breakdown.length > 0 && (
                                    <div className="ui segments">
                                      <div className="ui basic segment">
                                        {selectedReageAdjustment.reagedAccountPosition.arrears.breakdown.map(
                                          (arrear) => (
                                            <p key={arrear.age as any}>
                                              Age {arrear.age as any}: {toCurrency(arrear?.amount.value)}
                                            </p>
                                          )
                                        )}
                                      </div>
                                    </div>
                                  )}
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </>
                  )}
                </div>
              </ReageAdjustmentModal>
            </>
          )}
          <br />
          <button
            type="button"
            className="ui center aligned primary button"
            onClick={() => setRequestReagePopupVisible(true)}>
            <span>Request Re-age Adjustment</span>
          </button>
          <RequestReageAdjustmentModal
            show={requestReagePopupVisible}
            onHide={closeReagePopup}
            aria-labelledby="modal-label">
            <div className="ui basic segment">
              <div className="ui basic segment">
                <button className="ui right floated icon button">
                  <i className="close icon" onClick={() => closeRequestReagePopup()}></i>
                </button>
                <h3 className="ui left floated header">Request Re-age Adjustment</h3>
              </div>
              <br />
              <div className="ui basic segment">
                <div className="ui form">
                  <div className="ui labeled input">
                    <form className="ui form">
                      <label>Type</label>
                      <select
                        name="reageType"
                        className="ui dropdown"
                        defaultValue="Full"
                        onChange={(e) => reageTypeSelected(e.target.value)}>
                        <option key="Full" value="Full">
                          Full
                        </option>
                        <option key="Partial" value="Partial">
                          Partial
                        </option>
                      </select>
                      {showReageStrategy && (
                        <>
                          <label>Strategy</label>
                          <select
                            name="reageStrategy"
                            className="ui dropdown"
                            defaultValue="Forgive"
                            onChange={(e) => reageStrategySelected(e.target.value)}>
                            <option key="Forgive" value="Forgive">
                              Forgive
                            </option>
                            <option key="Rollup" value="Rollup">
                              Rollup
                            </option>
                          </select>
                        </>
                      )}
                      {showNewArrearLevel && (
                        <>
                          <label>New Arrears Level:</label>
                          <select
                            name="newArrearLevel"
                            className="ui dropdown"
                            onChange={(e) => setNewArrearLevel(parseInt(e.target.value))}>
                            {[...Array(accountLevel).keys()].map((level) => (
                              <option key={level} value={level}>
                                {level}
                              </option>
                            ))}
                          </select>
                        </>
                      )}
                    </form>
                  </div>
                  <div className="ui center aligned basic segment">
                    {error && (
                      <div className="ui negative message">
                        Re-age request was declined for reason(s): {error.detail}
                      </div>
                    )}
                    <button
                      type="button"
                      className="ui center aligned primary button"
                      onClick={requestReageAdjustmentHandler}
                      disabled={!!pending}>
                      {pending && (
                        <div className="ui active dimmer">
                          <div className="ui loader"></div>
                        </div>
                      )}
                      <span>Re-age</span>
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </RequestReageAdjustmentModal>
        </div>
      </div>
    </>
  );
}
